'use client';
import React, { useEffect, useRef } from 'react';

const plans = [
  {
    name: 'Starter',
    price: 0,
    period: 'Forever free',
    description: 'Your first lesson, on us. No card required.',
    features: [
      '1 full lesson — your choice',
      'All 12 camera angles included',
      'Offline download (1 lesson)',
      'Module completion badge',
    ],
    missing: ['CEU credits', 'Certification PDF', 'Unlimited access'],
    cta: 'Download Free',
    ctaStyle: 'glass-card glow-hover text-frost',
    featured: false,
  },
  {
    name: 'Pro',
    price: 29,
    period: 'per month',
    description: 'Built for stylists actively building their service menu.',
    features: [
      'All 847 techniques unlocked',
      'Offline download — up to 2.4 GB',
      'CEU credits (38 states)',
      'Module completion certificates',
      'LinkedIn credential integration',
      'New lessons weekly',
    ],
    missing: ['Salon team seats', 'Onboarding playbooks'],
    cta: 'Start Free Trial',
    ctaStyle: 'btn-primary text-slate-deep',
    featured: true,
    badge: 'Most Popular',
  },
  {
    name: 'Salon',
    price: 79,
    period: 'per month',
    description: 'Onboard your whole team without losing a booking.',
    features: [
      'Up to 8 stylist seats',
      'Everything in Pro',
      'Salon onboarding playbooks',
      'Progress tracking dashboard',
      'Priority support (4h response)',
      'Custom branding on certificates',
    ],
    missing: [],
    cta: 'Contact Sales',
    ctaStyle: 'glass-card sky-glow-hover text-frost',
    featured: false,
  },
];

export default function PricingSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="pricing" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="section-divider mb-24" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-DEFAULT mb-3 block">§ Pricing</span>
          <h2 className="font-sans text-4xl sm:text-5xl font-extrabold text-frost tracking-tight leading-tight mb-4">
            No hidden fees.<br />
            <span className="font-display italic font-light text-chrome">No commitment anxiety.</span>
          </h2>
          <p className="text-chrome text-base font-light max-w-lg mx-auto">
            Start with a free lesson. Upgrade when you're ready. Cancel any time.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
          {plans?.map((plan, i) => (
            <div
              key={plan?.name}
              className={`reveal rounded-2xl overflow-hidden transition-all duration-500 flex flex-col ${
                plan?.featured
                  ? 'pricing-featured' :'glass-card glow-hover'
              }`}
            >
              {/* Featured badge */}
              {plan?.featured && (
                <div className="bg-periwinkle-DEFAULT text-white text-[10px] font-bold tracking-widest uppercase text-center py-2">
                  ⚡ {plan?.badge}
                </div>
              )}

              <div className="p-6 sm:p-8 flex-1 flex flex-col">
                {/* Plan name */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-frost font-bold text-xl">{plan?.name}</h3>
                  {plan?.featured && (
                    <span className="badge-intermediate text-[10px] font-bold px-2.5 py-1 rounded-full">Recommended</span>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-end gap-2 mb-2">
                  <span className={`text-5xl font-extrabold ${plan?.featured ? 'gradient-text-sky' : 'text-frost'}`}>
                    {plan?.price === 0 ? 'Free' : `$${plan?.price}`}
                  </span>
                  {plan?.price > 0 && (
                    <span className="text-chrome-dim text-sm pb-2">{plan?.period}</span>
                  )}
                </div>
                <p className="text-chrome text-sm font-light mb-6">{plan?.description}</p>

                {/* Divider */}
                <div className="section-divider mb-6" />

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {plan?.features?.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <svg className="w-4 h-4 text-sky-DEFAULT shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span className="text-chrome text-sm font-light">{f}</span>
                    </li>
                  ))}
                  {plan?.missing?.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 opacity-40">
                      <svg className="w-4 h-4 text-chrome-dim shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                      <span className="text-chrome-dim text-sm font-light">{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href="#download"
                  onClick={(e) => { e?.preventDefault(); document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className={`${plan?.ctaStyle} flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm tracking-wide transition-all duration-300`}
                >
                  {plan?.cta}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Guarantee note */}
        <div className="reveal text-center mt-10">
          <p className="text-chrome-dim text-sm font-light">
            🔒 <span className="text-chrome">First lesson always free.</span> No credit card. No expiration. Cancel Pro or Salon anytime — no questions asked.
          </p>
        </div>
      </div>
    </section>
  );
}